import { usePositionStorage } from '@/hooks/usePositionStorage'
import { extractErrorMessage, safeRedirectPath } from '@/lib'
import { authVerifyEmail } from '@jobilla/nexus-sdk'
import type { AuthVerifyEmail403 } from '@jobilla/nexus-sdk'
import { isAxiosError } from 'axios'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from '../../utilities'
import { AuthErrorAlert } from './auth-error-alert'
import { AuthInput } from './auth-input'
import { AuthSubmitButton } from './auth-submit-button'
import { JobillaBrandLogo } from './jobilla-brand-logo'

export interface EmailVerificationFormProps {
	email: string
	pendingAuthenticationToken: string
	onOrganizationSelectionRequired?: (data: {
		pendingAuthenticationToken: string
		organizations: Array<{ id: string; name: string }>
	}) => void
}

export function EmailVerificationForm(
	{ email, pendingAuthenticationToken, onOrganizationSelectionRequired }: EmailVerificationFormProps,
) {
	const { t } = useTranslation('common')
	const { getRedirect, clearRedirect } = usePositionStorage()
	const [code, setCode] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)

	async function handleSubmit(event: React.FormEvent) {
		event.preventDefault()
		setError(null)
		setIsLoading(true)

		try {
			await authVerifyEmail({
				code: code.trim(),
				pendingAuthenticationToken,
			}, { withCredentials: true })

			// Verification sets the refresh cookie — reload to let auth provider pick up session
			const pendingRedirect = getRedirect()
			clearRedirect()
			window.location.href = safeRedirectPath(pendingRedirect, '/company')
		} catch (err) {
			if (isAxiosError<AuthVerifyEmail403>(err) && err.response?.status === 403) {
				const data = err.response.data
				if (
					data.code === 'organization_selection_required'
					&& data.pending_authentication_token
					&& data.organizations
					&& onOrganizationSelectionRequired
				) {
					onOrganizationSelectionRequired({
						pendingAuthenticationToken: data.pending_authentication_token,
						organizations: data.organizations,
					})
					return
				}
			}
			const status = isAxiosError(err) ? err.response?.status : undefined
			if (status && status >= 400 && status < 500) {
				logger.warn('Email verification failed', {
					category: 'auth',
					data: { status, action: 'verify-email' },
				})
			} else {
				logger.error(err, { category: 'auth', data: { action: 'verify-email' } })
			}
			setError(
				extractErrorMessage(err, t('auth.verifyEmail.errorDefault', 'Verification failed. Please try again.')),
			)
			setIsLoading(false)
		}
	}

	return (
		<div className="w-full max-w-md mx-auto px-8 py-16">
			<JobillaBrandLogo />

			<div className="pt-6">
				<h1 className="text-2xl font-semibold text-gray-800 tracking-[0.01em]">
					{t('auth.verifyEmail.title', 'Verify your email')}
				</h1>
				<p className="mt-2 text-gray-600">
					{t(
						'auth.verifyEmail.description',
						'We sent a verification code to {{email}}. Enter it below to continue.',
						{ email },
					)}
				</p>
			</div>

			<form onSubmit={handleSubmit} className="mt-6 space-y-6">
				<AuthErrorAlert message={error} />

				<AuthInput
					label={t('auth.verifyEmail.codeLabel', 'Verification code')}
					type="text"
					placeholder={t('auth.verifyEmail.codePlaceholder', 'Enter the 6-digit code')}
					value={code}
					onChange={setCode}
					required={true}
					autoComplete="one-time-code"
				/>

				<AuthSubmitButton isLoading={isLoading}>
					{t('auth.verifyEmail.submit', 'Verify')}
				</AuthSubmitButton>
			</form>
		</div>
	)
}
